import { pluck } from '@range.io/functional'
import { differenceInCalendarDays } from 'date-fns'

// ---------------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------------

const uniq = items => [...new Set(items)] // convert to Set and back to Array

// @sig filterByDayRange :: Number -> Date -> Boolean
const filterByDayRange = maxDayDifference => date => {
    const daysDiff = differenceInCalendarDays(new Date(), date)
    return daysDiff <= maxDayDifference
}

// ---------------------------------------------------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------------------------------------------------

// Does this Collaboration have ANY of the selected assignees?
// @sig filterByAssignee :: FilterSettings -> Collaboration -> Boolean
const filterByAssignee = filterSettings => collaboration => {
    if (!filterSettings.shouldFilterByAssignee) return true
    if (filterSettings.matchUnassigned) return collaboration.assignee === undefined // matchUnassigned inverts the logic
    if (filterSettings.assignees.length === 0) return true // the user hasn't yet selected any assignees to filter on: all pass
    return filterSettings.assignees.some(a => a.id === collaboration.assignee?.id) // has ANY required assignee
}

// Does this Collaboration have ANY of the selected categories?
// @sig filterByCategory :: FilterSettings -> Collaboration -> Boolean
const filterByCategory = filterSettings => collaboration =>
    !filterSettings.shouldFilterByCategory || filterSettings.categories.length === 0
        ? true
        : filterSettings.categories[collaboration.categoryId] // collaboration includes any of the selected categories

// Does this Collaboration have ALL selected tags?
// @sig filterByTags :: FilterSettings -> Collaboration -> Boolean
const filterByTags = filterSettings => collaboration => {
    if (!filterSettings.shouldFilterByTags) return true

    const uploadTags = collaboration.uploads.flatMap(u => u.tags)
    const collaborationPlusUploadTags = uniq(collaboration.tagNames.concat(uploadTags))

    if (filterSettings.matchUntagged) return collaborationPlusUploadTags.length === 0 // matchUntagged inverts the logic
    if (filterSettings.tags.length === 0) return true // the user hasn't yet selected any tags to filter on: all pass

    // use the tag ids, not the tags to compare to avoid issues with out-of-date Tags
    // collaboration or some upload has ANY of required tags
    const collaborationPlusUploadTagIds = new Set(pluck('id', collaborationPlusUploadTags))
    const userSelectedTagIds = new Set(pluck('id', filterSettings.tags))
    return userSelectedTagIds.intersection(collaborationPlusUploadTagIds).size > 0
}

// either it's not a task, or it matches one of the statuses selected by the user
// @sig filterStatuses :: FilterSettings -> Collaboration -> Boolean
const filterStatuses = filterSettings => collaboration =>
    filterSettings.showStatuses
        ? !collaboration.statusName || filterSettings.showStatuses.some(s => s.id === collaboration.statusName.id)
        : true

// Does this Collaboration is followed by ANY of the selected followers?
// @sig filterByFollower :: FilterSettings -> Collaboration -> Boolean
const filterByFollower = filterSettings => collaboration => {
    if (!filterSettings.shouldFilterByFollower) return true
    if (filterSettings.followers.length === 0) return true // the user hasn't yet selected any followers to filter on: all pass

    return filterSettings.followers.some(a => collaboration.followerIds.includes(a.id)) // has ANY required follower
}

// @sig isCreatedByMe :: String -> Collaboration -> Boolean
const isCreatedByMe = selectedUserId => collaboration => collaboration.createdBy.id === selectedUserId

// @sig isArchived :: FilterSettings -> Collaboration -> Boolean
const isArchived = filterSettings => collaboration => filterSettings.showArchivedPins || !collaboration.isArchived

// @sig isOverdue :: Collaboration -> Boolean
const isOverdue = collaboration => {
    if (!collaboration.dueDate) return false

    const now = new Date().toISOString()
    return new Date(collaboration.dueDate).toISOString() < now // show only tasks that are after due date
}

// @sig matchesAnyIdentifier :: [String] -> String -> Boolean
const matchesAnyIdentifier = selectedIdentifiers => identifier =>
    selectedIdentifiers.some(selectedIdentifier => selectedIdentifier === identifier)

// @sig filterByCreatedDate :: FilterSettings -> Collaboration -> Boolean
const filterByCreatedDate = filterSettings => collaboration =>
    filterSettings.shouldFilterByDateCreated && filterSettings.dateCreatedDayRange
        ? filterByDayRange(filterSettings.dateCreatedDayRange)(collaboration.createdAt)
        : true

// @sig filterByUpdateDate :: FilterSettings -> Collaboration -> Boolean
const filterByUpdateDate = filterSettings => collaboration =>
    filterSettings.shouldFilterByDateUpdated && filterSettings.dateUpdatedDayRange
        ? filterByDayRange(filterSettings.dateUpdatedDayRange)(collaboration.lastUpdateDate)
        : true

// @sig filterByIdentifier :: FilterSettings -> Collaboration -> Boolean
const filterByIdentifier = filterSettings => collaboration =>
    filterSettings.shouldFilterByIdentifiers && filterSettings.selectedIdentifiers.length
        ? matchesAnyIdentifier(filterSettings.selectedIdentifiers)(collaboration.identifier)
        : true

// @sig filterByCreatedByMe :: FilterSettings -> String -> Collaboration -> Boolean
const filterByCreatedByMe = (filterSettings, selectedUserId) => collaboration =>
    filterSettings.showOnlyCreatedByMe ? isCreatedByMe(selectedUserId)(collaboration) : true

// @sig filterByOverdue :: FilterSettings -> Collaboration -> Boolean
const filterByOverdue = filterSettings => collaboration =>
    filterSettings.showOnlyOverdueTasks ? isOverdue(collaboration) : true

/*
 * Apply common filtering logic to a list of collaborations based on filter settings
 * @sig filterCollaborations :: ([Collaboration], FilterSettings, String) -> [Collaboration]
 */
const filterCollaborations = (collaborations, filterSettings, selectedUserId) => {
    let result = collaborations

    result = result.filter(isArchived(filterSettings))
    result = result.filter(filterByAssignee(filterSettings))
    result = result.filter(filterByCategory(filterSettings))
    result = result.filter(filterByCreatedDate(filterSettings))
    result = result.filter(filterByUpdateDate(filterSettings))
    result = result.filter(filterByFollower(filterSettings))
    result = result.filter(filterByIdentifier(filterSettings))
    result = result.filter(filterByTags(filterSettings))
    result = result.filter(filterByCreatedByMe(filterSettings, selectedUserId))
    result = result.filter(filterByOverdue(filterSettings))
    result = result.filter(filterStatuses(filterSettings))

    return result
}

export default filterCollaborations
